// Empire Apps Chassis · identity-zkp · W3C Verifiable Credentials v2.0
// Data Integrity Proof with Ed25519Signature2020 cryptosuite. JSON-LD canonical
// hashing is approximated via URDNA2015-equivalent stable JSON serialisation,
// since full RDF canonicalisation in vanilla browser TS is heavy. For our
// threat model (single-issuer Empire ID), stable-JSON serialisation is
// equivalent in binding strength.

import type {
  DIDString,
  VerifiableCredentialV2,
  VerifiablePresentationV2,
  VCProofBlock,
  Ed25519PrivateKey,
  Ed25519PublicKey,
  AttributeValue,
} from './types.ts';
import { ed25519Sign, ed25519Verify, base64UrlEncode, base64UrlDecode } from './did.ts';
import { fromHex, toHex } from './commitment.ts';
import { resolveDid } from './did-resolver.ts';

const enc = new TextEncoder();
const VC_CONTEXT_V2 = 'https://www.w3.org/ns/credentials/v2';
const VP_TYPE = 'VerifiablePresentation';
const VC_TYPE = 'VerifiableCredential';

export interface IssueCredentialOptions {
  readonly issuer: DIDString;
  readonly issuerPrivateKey: Ed25519PrivateKey;
  readonly subject: DIDString;
  readonly type: readonly string[];
  readonly claims: Record<string, AttributeValue | Record<string, AttributeValue>>;
  readonly validFromIso?: string;
  readonly validUntilIso?: string;
}

export async function issueCredential(
  opts: IssueCredentialOptions,
): Promise<VerifiableCredentialV2> {
  const validFrom = opts.validFromIso ?? new Date().toISOString();
  const vcId = `urn:uuid:${crypto.randomUUID()}`;
  const vc: Omit<VerifiableCredentialV2, 'proof'> = {
    '@context': [VC_CONTEXT_V2, 'https://w3id.org/security/data-integrity/v2'],
    id: vcId,
    type: [VC_TYPE, ...opts.type],
    issuer: opts.issuer,
    validFrom,
    ...(opts.validUntilIso ? { validUntil: opts.validUntilIso } : {}),
    credentialSubject: { id: opts.subject, ...opts.claims },
  };
  const proof = await signProof(vc, opts.issuer, opts.issuerPrivateKey, 'assertionMethod');
  return { ...vc, proof };
}

export async function verifyCredential(
  vc: VerifiableCredentialV2,
): Promise<{ ok: boolean; reason: string }> {
  const issuerDid = typeof vc.issuer === 'string' ? vc.issuer : vc.issuer.id;
  const resolved = await resolveDid(issuerDid);
  if (!resolved.ok) return { ok: false, reason: `issuer-did-not-resolved:${resolved.reason}` };
  const ok = await verifyProof(vc, vc.proof, resolved.publicKey);
  return ok ? { ok: true, reason: 'verified' } : { ok: false, reason: 'signature-invalid' };
}

export interface PresentOptions {
  readonly holder: DIDString;
  readonly holderPrivateKey: Ed25519PrivateKey;
  readonly credentials: readonly VerifiableCredentialV2[];
  readonly challenge?: string;
  readonly domain?: string;
}

export async function createPresentation(
  opts: PresentOptions,
): Promise<VerifiablePresentationV2> {
  const vp: Omit<VerifiablePresentationV2, 'proof'> = {
    '@context': [VC_CONTEXT_V2, 'https://w3id.org/security/data-integrity/v2'],
    id: `urn:uuid:${crypto.randomUUID()}`,
    type: [VP_TYPE],
    holder: opts.holder,
    verifiableCredential: opts.credentials,
  };
  const proof = await signProof(vp, opts.holder, opts.holderPrivateKey, 'authentication');
  return { ...vp, proof };
}

export async function verifyPresentation(
  vp: VerifiablePresentationV2,
): Promise<{ ok: boolean; reason: string; credentialsVerified: number }> {
  const resolved = await resolveDid(vp.holder);
  if (!resolved.ok) return { ok: false, reason: `holder-did-not-resolved:${resolved.reason}`, credentialsVerified: 0 };
  const vpOk = await verifyProof(vp, vp.proof, resolved.publicKey);
  if (!vpOk) return { ok: false, reason: 'vp-signature-invalid', credentialsVerified: 0 };
  let count = 0;
  for (const vc of vp.verifiableCredential) {
    const r = await verifyCredential(vc);
    if (!r.ok) return { ok: false, reason: `embedded-vc-failed:${r.reason}`, credentialsVerified: count };
    count++;
  }
  return { ok: true, reason: 'verified', credentialsVerified: count };
}

// Used by identity-zkp/presentation.ts buildVP. Signs an unsigned ExtendedVP
// (which may include disclosureBundles + respondsTo) so the holder signature
// covers all disclosure surface, not just the base VP shape.
export async function signExtendedVP<T extends { holder: DIDString }>(
  doc: T,
  signerDid: DIDString,
  sk: Ed25519PrivateKey,
): Promise<T & { proof: VCProofBlock }> {
  const proof = await signProof(doc, signerDid, sk, 'authentication');
  return { ...doc, proof };
}

async function signProof(
  doc: unknown,
  signerDid: DIDString,
  sk: Ed25519PrivateKey,
  purpose: 'assertionMethod' | 'authentication',
): Promise<VCProofBlock> {
  const canonical = stableJson(doc);
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(canonical));
  const sig = await ed25519Sign(new Uint8Array(hash), sk);
  return {
    type: 'Ed25519Signature2020',
    cryptosuite: 'eddsa-2022',
    created: new Date().toISOString(),
    verificationMethod: `${signerDid}#keys-1` as DIDString,
    proofPurpose: purpose,
    proofValue: 'z' + base64UrlEncode(sig),
  };
}

async function verifyProof(
  doc: unknown,
  proof: VCProofBlock,
  publicKey: Ed25519PublicKey,
): Promise<boolean> {
  const obj = doc as Record<string, unknown>;
  const { proof: _omit, ...rest } = obj;
  void _omit;
  const canonical = stableJson(rest);
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(canonical));
  if (!proof.proofValue.startsWith('z')) return false;
  type Ed25519SignatureT = Uint8Array & { readonly __zkpBrand: 'Ed25519Signature' };
  const sig = base64UrlDecode(proof.proofValue.slice(1)) as Ed25519SignatureT;
  return ed25519Verify(new Uint8Array(hash), sig, publicKey);
}

export function stableJson(v: unknown): string {
  return JSON.stringify(sortKeys(v));
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(o).sort()) out[k] = sortKeys(o[k]);
    return out;
  }
  return v;
}
